import { create } from "zustand";
import {
  CustomerInfo,
  PurchasesPackage,
} from "@revenuecat/purchases-capacitor";

export type State = {
  paywallVisible:
    | boolean
    | {
        utmSource?: string;
        callback?: () => void;
        title?: string;
        subtitle?: string;
      };
  revenueCat?: {
    packages: PurchasesPackage[];
    customerInfo: CustomerInfo;
  };
  activeTab: "home" | "search" | "settings";
  isNewSession: boolean;
  device: {
    isMobile: boolean;
    width: number;
    height: number;
  };
  alertSelect?: {
    title?: string;
    buttonText?: string;
    defaultOption?: number;
    options: string[];
    onSelect?: (index: number) => void;
    onSelectAsync?: (index: number) => Promise<void>;
  };
  alertPromptSignIn?: {
    title: string;
    subtitle: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
    onGoogleSignIn?: () => void;
  };
  alertModal?: {
    title: string;
    loading?: boolean;
    subtitle: string;
    shouldPersistBackdropClick?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    onDismiss?: () => void;
    onLeftButtonPress?: () => void;
    button?: { text: string; onPress: () => void; destructive?: boolean };
    cancelText?: string;
  };
  alertPrompt?: {
    title: string;
    subtitle?: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
  };
  alertLanguagePicker?: {
    title?: string;
    selectedLanguage?: string;
    onSelect: (languageCode: string, languageName: string) => void;
    onDismiss?: () => void;
  };
};

type Actions = {
  setAlertModal: (payload: State["alertModal"]) => void;
  setActiveTab: (payload: State["activeTab"]) => void;
  setPaywallVisible: (payload: State["paywallVisible"]) => void;
  setIsNewSession: (isNewSession: boolean) => void;
  setAlertSelect: (payload: State["alertSelect"]) => void;
  setDevice: (payload: State["device"]) => void;
  setAlertPromptSignIn: (payload: State["alertPromptSignIn"]) => void;
  setAlertPrompt: (payload: State["alertPrompt"]) => void;
  setAlertLanguagePicker: (payload: State["alertLanguagePicker"]) => void;
  setRevenueCat: (payload: State["revenueCat"]) => void;
  reset: () => void;
};

const initialState: State = {
  paywallVisible: false,
  device: {
    isMobile: false,
    width: window.innerWidth,
    height: window.innerHeight,
  },
  isNewSession: true,
  activeTab: "home",
};

export const useUISlice = create<State & Actions>()((set, get) => ({
  ...initialState,
  setActiveTab: (payload) => {
    set({ activeTab: payload });
  },
  setIsNewSession: (isNewSession) => {
    set({ isNewSession });
  },
  setRevenueCat: (payload) => {
    set({ revenueCat: payload });
  },
  setAlertSelect: (payload) => {
    set({ alertSelect: payload });
  },
  setPaywallVisible: (payload) => {
    set({ paywallVisible: payload });
  },
  setDevice: (payload) => {
    set({ device: payload });
  },
  setAlertPromptSignIn: (payload) => {
    set({ alertPromptSignIn: payload });
  },
  setAlertModal: (payload) => {
    set({ alertModal: payload });
  },
  setAlertPrompt: (payload) => {
    set({ alertPrompt: payload });
  },
  setAlertLanguagePicker: (payload) => {
    set({ alertLanguagePicker: payload });
  },
  reset: () => {
    set({ ...initialState });
  },
}));
